import type { FC } from 'react';
import Link from 'next/link';
import clsx from 'clsx';

import { Heading, Body } from 'components/text';
import { Image } from 'components/ImageComponent';

import styles from './_PressArticleCard.module.scss';
import DateService from 'services/DateService/DateService';
import { BaseText } from 'components/text/BaseText';
import { navigationPaths } from 'data/constants';
import ArrowUpRightIcon from 'components/icons/ArrowUpRight';

import type { PressArticleCardProps } from './types';

const PressArticleCard: FC<PressArticleCardProps> = ({
  image,
  title,
  description,
  date,
  className,
  id,
  digitalUrl,
}) => {
  // digitalUrl means external link, so we use it as href, otherwise we use the internal link
  const cardHref =
    digitalUrl || navigationPaths.PRESS_DETAIL.replace(':id', `${id}`);

  const linkTarget = digitalUrl ? '_blank' : '_self';

  // security rel attribute for external links
  const linkRel = digitalUrl ? 'noopener noreferrer' : undefined;

  return (
    <article className={clsx(styles.card, className)}>
      <Link href={cardHref} className={styles.card__link} passHref>
        <a
          target={linkTarget}
          aria-label={title}
          href={cardHref}
          rel={linkRel}
          className={styles.card__link}
          title={title}
        >
          <figure className={styles.card__image}>
            <Image
              src={image ?? ''}
              alt={title}
              style={{
                width: '100%',
                height: '100%',
                aspectRatio: '16/9',
                objectFit: 'contain',
                maxWidth: '100%',
                maxHeight: '100%',
                backgroundColor: '#ffffff',
              }}
            />
          </figure>
          <div className={styles.card__content}>
            <time>
              <BaseText
                weight="demiBold"
                className={styles.card__content__date}
              >
                {DateService.getFormattedDateString(date)}
              </BaseText>
            </time>
            <div className={styles.card__content__title}>
              <Heading as="h1" weight="demiBold" fontSize="18px">
                {title}
              </Heading>
              <ArrowUpRightIcon />
            </div>
            <Body className={styles.card__content__description}>
              {description}
            </Body>
          </div>
        </a>
      </Link>
    </article>
  );
};

export default PressArticleCard;
